import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, MONTHS } from "../api";
import type { Me, MenuDay, MenuSummary } from "../types";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import { DOW, DOW_LONG, todayIso, weeksOf } from "../lib/calendar";
import {
  Button,
  EmptyState,
  Field,
  PageHeader,
  Skeleton,
} from "../components/ui";

interface ResyncItem {
  student: string;
  status: "ok" | "error";
}
function resyncNote(items: ResyncItem[] | undefined): string {
  if (!items || items.length === 0) return "";
  const ok = items.filter((i) => i.status === "ok").length;
  const bad = items.filter((i) => i.status === "error");
  let s = ` · re-synced ${ok} order${ok === 1 ? "" : "s"}`;
  if (bad.length) s += `, ${bad.length} failed (${bad.map((b) => b.student).join(", ")})`;
  return s;
}

function Dish({ kind, text }: { kind: "hot" | "swap" | "veg"; text: string }) {
  const label = kind === "hot" ? "Hot" : kind === "swap" ? "Swap" : "Veggie";
  return (
    <div className={`dish ${kind}`}>
      <span className="tag">{label}</span>
      <span className="name">{text}</span>
    </div>
  );
}

function MenuGrid({
  year,
  month,
  days,
}: {
  year: number;
  month: number;
  days: Record<string, MenuDay>;
}) {
  const today = todayIso();
  return (
    <div className="menu-grid">
      {DOW.map((d) => (
        <div key={d} className="col-head">
          {d}
        </div>
      ))}
      {weeksOf(year, month).flatMap((week, wi) =>
        week.map((date, di) => {
          if (!date) return <div key={`${wi}-${di}`} className="cell empty" />;
          const day = days[date];
          return (
            <div
              key={date}
              className={`cell ${date === today ? "today" : ""} ${day ? "" : "noserve"}`}
            >
              <div className="cell-head">
                <span className="dow">{DOW_LONG[di]}</span>
                <span className="dnum">{Number(date.slice(8))}</span>
              </div>
              {day ? (
                <div className="dishes">
                  {day.hot && <Dish kind="hot" text={day.hot} />}
                  {day.swap && <Dish kind="swap" text={day.swap} />}
                  {day.veggie && <Dish kind="veg" text={day.veggie} />}
                  {day.sides?.length ? (
                    <div className="dish side">
                      <span className="tag">Sides</span>
                      <span className="name">{day.sides.join(", ")}</span>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="noserve-label">No lunch service</div>
              )}
            </div>
          );
        }),
      )}
    </div>
  );
}

export function Menus() {
  const me = useOutletContext<Me>();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);
  const [editing, setEditing] = useState<MenuSummary | null>(null);

  const menus = useQuery({
    queryKey: ["menus"],
    queryFn: () =>
      api.get<{ menus: MenuSummary[]; nutrislice: boolean }>("/menus"),
  });
  const nutrislice = menus.data?.nutrislice ?? false;

  const adminActions = me.isAdmin && (
    <div className="cluster">
      {nutrislice && (
        <Button icon="refresh" onClick={() => setShowRefresh(true)}>
          Update from Nutrislice
        </Button>
      )}
      <Button
        variant={nutrislice ? "ghost" : "primary"}
        icon="upload"
        onClick={() => setShowUpload(true)}
      >
        Upload PDF
      </Button>
    </div>
  );

  const rows = [...(menus.data?.menus ?? [])].sort(
    (a, b) => b.year - a.year || b.month - a.month,
  );
  const selected = rows.find((m) => m.id === selectedId) ?? rows[0];

  return (
    <>
      <PageHeader
        title="Menu"
        desc={
          me.isAdmin
            ? "Pull the month straight from Nutrislice, or upload the PDF as a backup."
            : "The monthly lunch menu. Hot, Swap, and Veggie are the three entrée choices on the order form."
        }
        action={adminActions}
      />

      {menus.isLoading ? (
        <Skeleton h={320} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="utensils"
          title="No menu loaded yet"
          action={adminActions}
        >
          {me.isAdmin
            ? nutrislice
              ? "Click “Update from Nutrislice” to pull this month's menu."
              : "Upload the Nutrislice PDF for a month to get started."
            : "Check back once an admin loads this month's menu."}
        </EmptyState>
      ) : (
        <div className="bleed">
        <div className="bleed-inner">
        <div className="card">
          <div className="between">
            <h2>{selected.school} lunch</h2>
            <div className="cluster">
              {rows.length > 1 && (
                <select
                  value={selected.id}
                  onChange={(e) => setSelectedId(Number(e.target.value))}
                >
                  {rows.map((m) => (
                    <option key={m.id} value={m.id}>
                      {MONTHS[m.month - 1]} {m.year}
                    </option>
                  ))}
                </select>
              )}
              <a
                className="btn ghost sm"
                href={selected.hasPdf ? `/api/menus/${selected.id}/file` : selected.webUrl}
                target="_blank"
                rel="noreferrer"
              >
                <Icon name="external" size={14} />
                {selected.hasPdf ? "PDF" : "Nutrislice"}
              </a>
              {me.isAdmin && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon="pencil"
                  onClick={() => setEditing(selected)}
                >
                  Edit dishes
                </Button>
              )}
            </div>
          </div>
          <p className="sub" style={{ marginTop: 4 }}>
            {MONTHS[selected.month - 1]} {selected.year}
          </p>
          <MenuGrid
            year={selected.year}
            month={selected.month}
            days={selected.days}
          />
        </div>
        </div>
        </div>
      )}

      {showUpload && (
        <UploadModal
          me={me}
          onClose={() => setShowUpload(false)}
          onDone={(msg) => {
            setShowUpload(false);
            toast.success(msg);
          }}
        />
      )}

      {showRefresh && (
        <RefreshModal
          me={me}
          onClose={() => setShowRefresh(false)}
          onDone={(msg) => {
            setShowRefresh(false);
            toast.success(msg);
          }}
        />
      )}

      {editing && (
        <EditModal
          menu={editing}
          onClose={() => setEditing(null)}
          onDone={(msg) => {
            setEditing(null);
            toast.success(msg);
          }}
        />
      )}
    </>
  );
}

function RefreshModal({
  me,
  onClose,
  onDone,
}: {
  me: Me;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const now = new Date();
  const [school, setSchool] = useState(me.schoolName);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [err, setErr] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () =>
      api.post<{ dayCount: number; resynced: ResyncItem[] }>(
        "/menus/nutrislice",
        { school, year, month },
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["menus"] });
      qc.invalidateQueries({ queryKey: ["submissions"] });
      onDone(`Pulled ${r.dayCount} days from Nutrislice` + resyncNote(r.resynced));
    },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Modal
      title="Update from Nutrislice"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="refresh"
            loading={run.isPending}
            onClick={() => run.mutate()}
          >
            Fetch menu
          </Button>
        </>
      }
    >
      <p className="muted small" style={{ marginBottom: 14 }}>
        Pulls the month's Hot / Swap / Veggie dishes from the school's live
        Nutrislice feed. Any uploaded PDF for the month is kept as a backup.
      </p>
      {err && (
        <div className="toast error" style={{ marginBottom: 14 }}>
          <Icon name="alert-circle" size={18} />
          <span className="grow">{err}</span>
        </div>
      )}
      <div className="row">
        <Field label="School">
          <input
            type="text"
            value={school}
            onChange={(e) => setSchool(e.target.value)}
          />
        </Field>
        <Field label="Year">
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </Field>
        <Field label="Month">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}

function UploadModal({
  me,
  onClose,
  onDone,
}: {
  me: Me;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const now = new Date();
  const [school, setSchool] = useState(me.schoolName);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.set("file", file!);
      fd.set("school", school);
      fd.set("year", String(year));
      fd.set("month", String(month));
      return api.upload<{
        source: string;
        warnings: string[];
        resynced: ResyncItem[];
      }>("/menus", fd);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["menus"] });
      qc.invalidateQueries({ queryKey: ["submissions"] });
      onDone(
        `Parsed via ${r.source}` +
          (r.warnings.length ? ` (${r.warnings.length} warning)` : "") +
          resyncNote(r.resynced),
      );
    },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Modal
      title="Upload menu"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="upload"
            loading={upload.isPending}
            disabled={!file}
            onClick={() => upload.mutate()}
          >
            Upload &amp; parse
          </Button>
        </>
      }
    >
      {err && (
        <div className="toast error" style={{ marginBottom: 14 }}>
          <Icon name="alert-circle" size={18} />
          <span className="grow">{err}</span>
        </div>
      )}
      <div className="row">
        <Field label="School">
          <input
            type="text"
            value={school}
            onChange={(e) => setSchool(e.target.value)}
          />
        </Field>
        <Field label="Year">
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </Field>
        <Field label="Month">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Nutrislice PDF">
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </Field>
    </Modal>
  );
}

function EditModal({
  menu,
  onClose,
  onDone,
}: {
  menu: MenuSummary;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [days, setDays] = useState<Record<string, MenuDay>>(menu.days);
  const dates = Object.keys(days).sort();

  const set = (date: string, f: "hot" | "swap" | "veggie", v: string) =>
    setDays((d) => ({ ...d, [date]: { ...d[date], [f]: v || null } }));
  const setSides = (date: string, v: string) =>
    setDays((d) => ({
      ...d,
      [date]: {
        ...d[date],
        sides: v
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      },
    }));

  const save = useMutation({
    mutationFn: () =>
      api.put<{ resynced: ResyncItem[] }>(`/menus/${menu.id}`, { days }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["menus"] });
      qc.invalidateQueries({ queryKey: ["submissions"] });
      onDone("Menu saved" + resyncNote(r.resynced));
    },
  });

  return (
    <Modal
      title={`Edit ${MONTHS[menu.month - 1]} ${menu.year} dishes`}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save dishes
          </Button>
        </>
      }
    >
      <div style={{ overflow: "auto", maxHeight: "56vh" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Day</th>
              <th>Hot</th>
              <th>Swap</th>
              <th>Veggie</th>
              <th>Sides (comma-separated)</th>
            </tr>
          </thead>
          <tbody>
            {dates.map((date) => (
              <tr key={date}>
                <td className="mono">{date.slice(5)}</td>
                {(["hot", "swap", "veggie"] as const).map((f) => (
                  <td key={f}>
                    <input
                      type="text"
                      value={days[date][f] ?? ""}
                      onChange={(e) => set(date, f, e.target.value)}
                    />
                  </td>
                ))}
                <td>
                  <input
                    type="text"
                    value={(days[date].sides ?? []).join(", ")}
                    onChange={(e) => setSides(date, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
